// @workflow assistant.ui-tools, assistant.attachments
/**
 * The register UI tool: `ui.registers.open_import { registerId, attachmentId, facilityId? }`.
 * A file the user attached in the chat opens in the app's real Register Import screen
 * (Register Management → the register's "Import from Excel" dialog) with that file already loaded,
 * so the user maps the columns and imports there with the screen's own controls. It never imports.
 *
 * - The file is the original File of the chat upload, kept in page memory (attachmentFiles.js);
 *   after a reload it is gone: not_available "The file isn't on this page anymore. Attach it again."
 * - Needs register import (CAPABILITY.REGISTERS_IMPORT): the runner answers noAccess otherwise.
 * - The page takes the file through actions/registerImport.js and says which register it opened.
 *   `facilityId` is accepted (checked) but not needed: Register Management lists every register.
 *
 * Success: `{ opened: true, register: <name> }`.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable } from './uiToolContract.js';
import { cleanFacilityId } from './viewerArgs.js';
import { fileFor } from './attachmentFiles.js';
import { REGISTER_IMPORT_ROUTE, REGISTER_IMPORT_OUTCOME, offerImportFile, declineImportFile } from '../../../actions/registerImport.js';
import { CAPABILITY } from '../../../actions/permissions.js';

export const REGISTER_LIMITS = Object.freeze({ MAX_ID_LENGTH: 64 });

/** Plain words for the model. */
export const REGISTER_DETAIL = Object.freeze({
  FILE_GONE: "The file isn't on this page anymore. Attach it again.",
  NOT_FOUND: "AVA couldn't find that register on the Register Management page.",
  NOT_OPENED: "The Register Import screen didn't open. Open Register Management and import the file there.",
});

const ID = /^[A-Za-z0-9_-]+$/;
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

function cleanId(value) {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  return id && id.length <= REGISTER_LIMITS.MAX_ID_LENGTH && ID.test(id) ? id : null;
}

/** `{ registerId, attachmentId, facilityId? }` → `{ registerId, attachmentId, facilityId }` or null. */
export function checkOpenImportArgs(args) {
  if (!isPlainObject(args)) return null;
  const registerId = cleanId(args.registerId);
  const attachmentId = cleanId(args.attachmentId);
  const facilityId = cleanFacilityId(args.facilityId);
  if (!registerId || !attachmentId || facilityId === undefined) return null;
  return { registerId, attachmentId, facilityId };
}

/** Nothing to prepare but the file: without it the screen would open empty. */
const fileStillHere = (host, { attachmentId }) => (fileFor(attachmentId) ? null : notAvailable(REGISTER_DETAIL.FILE_GONE));

async function openImport({ registerId, attachmentId }, host) {
  const file = fileFor(attachmentId);
  if (!file) return notAvailable(REGISTER_DETAIL.FILE_GONE);
  const answer = offerImportFile({ registerId, file, signal: (host && host.signal) || null });
  try {
    await host.navigate({ name: REGISTER_IMPORT_ROUTE.NAME, query: { registerId, view: REGISTER_IMPORT_ROUTE.VIEW } });
  } catch (err) {
    declineImportFile(registerId); // the offer ends with the navigation, not on the timer
    throw err;
  }
  const { outcome, register } = await answer;
  if (outcome === REGISTER_IMPORT_OUTCOME.OPENED) return ok({ opened: true, register });
  return notAvailable(outcome === REGISTER_IMPORT_OUTCOME.NOT_FOUND ? REGISTER_DETAIL.NOT_FOUND : REGISTER_DETAIL.NOT_OPENED);
}

/** Registry entries. `capability`: the runner checks `host.can` after `check`, before `ready`. */
export const REGISTER_ACTIONS = Object.freeze({
  [UI_TOOL.REGISTERS_OPEN_IMPORT]: {
    capability: CAPABILITY.REGISTERS_IMPORT,
    check: checkOpenImportArgs,
    ready: fileStillHere,
    run: openImport,
  },
});
